import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, MapPin, DollarSign, ShoppingBag, Star, UtensilsCrossed, TrendingUp, User } from 'lucide-react';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils-helpers';

export default function VendorDetail360() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/admin/vendors/${vendorId}/360`);
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [vendorId]);

  if (loading) return <div className="p-6">Loading...</div>;
  if (!data) return <div className="p-6">Vendor not found</div>;

  const vendor = data.vendor;
  const stats = data.statistics;

  const getStatusColor = (status) => {
    const colors = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      suspended: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/admin/vendors')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold" data-font="display">360° Vendor View</h1>
            <p className="text-muted-foreground">Complete business profile for {vendor.name}</p>
          </div>
        </div>
      </div>

      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex gap-6">
              {vendor.logo_url ? (
                <img src={vendor.logo_url} alt={vendor.name} className="w-24 h-24 rounded-lg object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-lg bg-primary/10 flex items-center justify-center">
                  <UtensilsCrossed className="h-12 w-12 text-primary" />
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold">{vendor.name}</h2>
                    {vendor.vendor_number && (
                      <Badge 
                        variant="outline" 
                        className="font-mono text-xs border-[hsl(351,96%,31%)] text-[hsl(351,96%,31%)] bg-[hsl(351,96%,31%,0.05)]"
                        data-testid="vendor-id-badge"
                      >
                        {vendor.vendor_number}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground">{vendor.category}</p>
                  <p className="text-muted-foreground flex items-center gap-2 mt-1">
                    <MapPin className="h-4 w-4" /> {vendor.address}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Badge className={getStatusColor(vendor.status)}>{vendor.status}</Badge>
                  {vendor.is_open ? (
                    <Badge className="bg-green-100 text-green-800">● Open Now</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-800">Closed</Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    {vendor.rating?.toFixed(1) || '0.0'} ({vendor.total_ratings || 0} reviews)
                  </span>
                  <span>{vendor.delivery_time}</span>
                  <span>{formatCurrency(vendor.delivery_fee)} delivery</span>
                </div>
                {data.owner && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Owner: {data.owner.name} ({data.owner.email})
                  </p>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4 text-right">
              <div>
                <p className="text-2xl font-bold">{stats.total_orders}</p>
                <p className="text-sm text-muted-foreground">Total Orders</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.total_revenue)}</p>
                <p className="text-sm text-muted-foreground">Revenue</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.menu_item_count}</p>
                <p className="text-sm text-muted-foreground">Menu Items</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active_orders}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="orders" className="w-full">
        <TabsList>
          <TabsTrigger value="orders"><ShoppingBag className="h-4 w-4 mr-2" />Orders</TabsTrigger>
          <TabsTrigger value="menu"><UtensilsCrossed className="h-4 w-4 mr-2" />Menu</TabsTrigger>
          <TabsTrigger value="reviews"><Star className="h-4 w-4 mr-2" />Reviews</TabsTrigger>
          <TabsTrigger value="revenue"><TrendingUp className="h-4 w-4 mr-2" />Revenue</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Order History</CardTitle>
            </CardHeader>
            <CardContent>
              {data.orders.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No orders found</p>
              ) : (
                <div className="space-y-3">
                  {data.orders.slice(0, 20).map(order => (
                    <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{order.id}</p>
                        <p className="text-sm text-muted-foreground">{order.user_name}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(order.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(order.total)}</p>
                        <Badge>{order.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="menu" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Menu Items ({data.menu_items.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.menu_items.map(item => (
                  <div key={item.id} className="flex gap-4 p-4 border rounded-lg">
                    {item.image_url && (
                      <img src={item.image_url} alt={item.name} className="w-20 h-20 rounded object-cover" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-muted-foreground">{item.category}</p>
                      <p className="font-medium text-primary mt-1">{formatCurrency(item.price)}</p>
                      {!item.is_available && <Badge className="mt-1 bg-red-100 text-red-800">Unavailable</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Customer Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="text-center">
                    <p className="text-4xl font-bold">{stats.average_rating.toFixed(1)}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`h-4 w-4 ${i < Math.round(stats.average_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{stats.review_count} reviews</p>
                  </div>
                </div>
                {data.reviews.map(review => (
                  <div key={review.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium">{review.user_name}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`h-3 w-3 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{formatDate(review.created_at)}</p>
                    </div>
                    <p className="text-sm">{review.comment}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="revenue" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.transactions.map(txn => (
                  <div key={txn.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">{txn.type.replace('_', ' ')}</p>
                      <p className="text-sm text-muted-foreground">{txn.description || 'No description'}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-medium ${txn.type === 'vendor_payout' ? 'text-green-600' : 'text-gray-600'}`}>
                        {txn.type === 'vendor_payout' ? '+' : ''}{formatCurrency(txn.amount)}
                      </p>
                      <Badge className={txn.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {txn.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
